import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, Plus, Trash2, ListChecks } from "lucide-react";

export const HabitsCard = ({ onTick }) => {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/habits").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const { habits, max } = data;

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError("");
    try {
      const { data: next } = await api.post("/habits", { name });
      setData(next);
      setName("");
      setAdding(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not add habit.");
    }
  };

  const tick = async (h) => {
    setData((await api.post(`/habits/${h.id}/tick`, {})).data);
    onTick?.();
  };
  const remove = async (h) => setData((await api.delete(`/habits/${h.id}`)).data);

  return (
    <section className="mb-10" data-testid="habits-card">
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <p className="eyebrow">Your own small wins</p>
          <h2 className="font-display font-black text-2xl md:text-3xl mt-1">
            DAILY HABITS <span style={{ color: "#FF0055" }}>({habits.filter((h) => h.today_done).length}/{habits.length})</span>
          </h2>
        </div>
        {habits.length < max && !adding && (
          <button className="btn-ghost inline-flex items-center gap-1.5 text-xs" onClick={() => setAdding(true)} data-testid="habit-add-btn">
            <Plus size={14} /> Add habit
          </button>
        )}
      </div>

      {adding && (
        <form onSubmit={add} className="card flex gap-3 items-center mb-3" data-testid="habit-add-form">
          <input className="input" autoFocus placeholder="e.g. 10k steps · 2L water · Lights out by 10:30" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} data-testid="habit-name-input" />
          <button type="submit" className="btn-primary" disabled={!name.trim()} data-testid="habit-save-btn">Add</button>
          <button type="button" className="btn-ghost" onClick={() => { setAdding(false); setName(""); setError(""); }} data-testid="habit-cancel-btn">Cancel</button>
        </form>
      )}
      {error && <p className="text-xs mb-3" style={{ color: "#FF0055" }} data-testid="habit-error">{error}</p>}

      {habits.length === 0 && !adding && (
        <button className="card card-interactive w-full text-left flex items-center gap-4" onClick={() => setAdding(true)} data-testid="habit-empty">
          <div className="w-11 h-11 flex items-center justify-center shrink-0" style={{ background: "#1A1A1A", border: "1px solid #3f3f46" }}>
            <ListChecks size={20} color="#a1a1aa" />
          </div>
          <div>
            <p className="eyebrow">Beside the 4 commitments</p>
            <p className="font-display font-bold uppercase mt-1">Add your first small daily habit</p>
          </div>
        </button>
      )}

      <div className="space-y-3 stagger">
        {habits.map((h) => (
          <div key={h.id} className="card flex items-center gap-4" data-testid={`habit-${h.id}`} style={h.today_done ? { borderColor: "#FF0055" } : {}}>
            <button
              onClick={() => tick(h)}
              data-testid={`habit-tick-${h.id}`}
              aria-pressed={h.today_done}
              className="w-11 h-11 flex items-center justify-center shrink-0 transition-colors"
              style={{ background: h.today_done ? "#FF0055" : "#1A1A1A", border: `1px solid ${h.today_done ? "#FF0055" : "#3f3f46"}` }}
            >
              {h.today_done ? <Check size={20} strokeWidth={3} color="white" /> : <span className="w-2 h-2 rounded-full" style={{ background: "#3f3f46" }} />}
            </button>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold uppercase text-sm md:text-base truncate">{h.name}</p>
              <div className="flex items-center gap-1.5 mt-2" data-testid={`habit-trail-${h.id}`}>
                {h.trail.map((t) => (
                  <span key={t.date} title={t.date} className="w-2.5 h-2.5 rounded-full" style={{ background: t.done ? "#FF0055" : "#27272A" }} />
                ))}
                <span className="text-[10px] text-zinc-500 uppercase tracking-widest ml-2">{h.week_count}/7 this week</span>
              </div>
            </div>
            <button onClick={() => remove(h)} className="text-zinc-600 hover:text-white transition-colors" data-testid={`habit-delete-${h.id}`} aria-label="Remove habit">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};
